/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * CART-13 — Order-level discount lines in the Cart Summary.
 *
 * Renders one brand-red row per entry in `basket.orderPriceAdjustments[]`.
 * Sorted by `priceAdjustmentId` ascending for stable ordering across requests.
 *
 * - Positive adjustments (fees) use `text-muted-foreground`.
 * - Negative adjustments (discounts) use the shared `text-promo-discount` token.
 * - Returns `null` when the array is empty.
 * - Plain-text only — no `dangerouslySetInnerHTML`.
 * - Visual token matches CART-12 (`<CartLinePromoCallouts/>`) and CART-14 (`<ShippingPromoLine/>`).
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/currency';
import { useOrderSummaryContext } from './context';

export interface OrderDiscountLinesProps {
    adjustments: Array<{
        priceAdjustmentId?: string;
        promotionId?: string;
        itemText?: string;
        calloutMsg?: string;
        price?: number;
    }>;
}

export function OrderDiscountLines({ adjustments }: OrderDiscountLinesProps): ReactElement | null {
    const { t, i18n } = useTranslation('cart');
    const { currency, promotions } = useOrderSummaryContext();

    const sorted = [...adjustments].sort((a, b) =>
        (a.priceAdjustmentId ?? '').localeCompare(b.priceAdjustmentId ?? '')
    );

    if (sorted.length === 0) return null;

    return (
        <div className="border-t border-input py-2">
            <p className="text-xs font-semibold leading-4 text-foreground">
                {t('cartSummary.discount.promotionalDiscounts')}
            </p>
            <div className="mt-1">
                {sorted.map((adjustment) => {
                    const price = adjustment.price ?? 0;
                    const isDiscount = price < 0;
                    const colorClass = isDiscount ? 'text-promo-discount' : 'text-muted-foreground';
                    const formattedAmount = formatCurrency(price, i18n.language, currency);
                    // Prefer the BM-authored calloutMsg (from the Promotions API via context),
                    // fall back to basket-level calloutMsg for forward-compatibility,
                    // then itemText as the final fallback.
                    const label =
                        (adjustment.promotionId ? promotions[adjustment.promotionId]?.calloutMsg : undefined) ??
                        adjustment.calloutMsg ??
                        adjustment.itemText ??
                        '';

                    return (
                        <div
                            key={adjustment.priceAdjustmentId}
                            data-testid="order-discount-line"
                            aria-label={(t as (key: string, opts?: Record<string, string>) => string)(
                                'cartSummary.discount.lineFormat',
                                { label, amount: formattedAmount }
                            )}
                            className="flex items-center justify-between py-1">
                            <span className={cn(colorClass)}>{label}</span>
                            <span className={cn(colorClass)}>{formattedAmount}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
